import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { RotateCcw, SlidersHorizontal } from "@fleet/icons";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  Switch,
  TextField,
} from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { adminQueryKeys, type components } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";

type ErrorModel = components["schemas"]["ErrorModel"];
type AdminConfig = components["schemas"]["AdminConfigResponse"];
type OperationalSettings = components["schemas"]["OperationalSettings"];
type ConfigKeyInfo = components["schemas"]["ConfigKeyInfo"];

/**
 * Logical key → resolved DTO reader.
 *
 * The panel renders from the CATALOG the backend serves, so adding a
 * knob doesn't require touching the layout; the only thing to maintain is this
 * key (snake_case) → DTO field (camelCase) translation.
 */
const READ: Record<string, (s: OperationalSettings) => number> = {
  "limits.rate_limit_rpm": (s) => s.limits.rateLimitRpm,
  "limits.avatar_max_upload_bytes": (s) => s.limits.avatarMaxUploadBytes,
  "limits.avatar_max_uploads_per_hour": (s) => s.limits.avatarMaxUploadsPerHour,
  "limits.media_max_file_bytes": (s) => s.limits.mediaMaxFileBytes,
  "limits.media_signed_url_ttl_secs": (s) => s.limits.mediaSignedUrlTtlSecs,
  "limits.media_upload_abandon_hours": (s) => s.limits.mediaUploadAbandonHours,
  "retention.trash_purge_days": (s) => s.retention.trashPurgeDays,
  "retention.vod_days": (s) => s.retention.vodDays,
  "retention.chat_days": (s) => s.retention.chatDays,
  "retention.account_deletion_grace_days": (s) => s.retention.accountDeletionGraceDays,
  "retention.audit_log_days": (s) => s.retention.auditLogDays,
  "security.login_max_failed_attempts": (s) => s.security.loginMaxFailedAttempts,
  "security.login_lockout_secs": (s) => s.security.loginLockoutSecs,
  "academy.checkin_grace_minutes": (s) => s.academy.checkinGraceMinutes,
  "academy.live_join_window_min": (s) => s.academy.liveJoinWindowMin,
  "academy.live_join_grace_after_min": (s) => s.academy.liveJoinGraceAfterMin,
  "academy.cohort_max_capacity": (s) => s.academy.cohortMaxCapacity,
  "billing.rate_max_age_hours": (s) => s.billing.rateMaxAgeHours,
  "billing.payment_intent_ttl_minutes": (s) => s.billing.paymentIntentTtlMinutes,
  "billing.dunning_lead_days": (s) => s.billing.dunningLeadDays,
};

const BOOL_READ: Record<string, (s: OperationalSettings) => boolean> = {
  "modules.live_sessions_enabled": (s) => s.modules.liveSessionsEnabled,
  "modules.media_uploads_enabled": (s) => s.modules.mediaUploadsEnabled,
  "academy.enrollment_requires_approval": (s) => s.academy.enrollmentRequiresApproval,
  "academy.enrollment_force_approval": (s) => s.academy.enrollmentForceApproval,
};

const LIST_READ: Record<string, (s: OperationalSettings) => string[]> = {
  "i18n.available_locales": (s) => s.i18n.availableLocales,
};

/** Catalog groups, in the order they're rendered. */
const GROUPS = [
  "limits",
  "retention",
  "security",
  "i18n",
  "modules",
  "academy",
  "billing",
] as const;

/**
 * **Platform operational configuration** panel (CONFIG_OVER_CODE).
 *
 * Edits the same source as the env: every change persists an override in
 * `app_config` that overrides the env **live**, and "Reset to env" deletes it and
 * hands back control. Each control shows the effective value, its ORIGIN (`env /
 * default` vs `override`), its equivalent env var and the **explicit unit** — the
 * `STORAGE_MB` vs `storage_bytes` gotcha is avoided by printing the unit, not
 * relying on memory.
 *
 * `org`-level keys are marked as such: this sets the **ceiling** for
 * all academies; each academy decides within it from its own panel
 * (`/api/org/config`). Lowering a ceiling here NARROWS all of them at once.
 */
export function AppConfigManager() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  const canWrite = () => can("settings.write");

  const query = createApiQuery<AdminConfig, ErrorModel>(
    () => adminQueryKeys.appConfig(),
    async () => api.GET("/api/admin/config", {}),
    () => ({ staleTime: 5000 }),
  );

  const upsertMutation = createApiMutation(async (vars: { key: string; value: unknown }) =>
    api.POST("/api/admin/config", { body: vars }),
  );

  const deleteMutation = createApiMutation(async (vars: { key: string }) =>
    api.DELETE("/api/admin/config/{key}", { params: { path: { key: vars.key } } }),
  );

  const resolved = () => query.data?.resolved;
  const envDefaults = () => query.data?.envDefaults;
  const catalog = createMemo(() => query.data?.catalog ?? []);
  const overriddenKeys = createMemo(() => new Set((query.data?.overrides ?? []).map((o) => o.key)));

  const refetch = () => {
    void query.refetch();
  };

  const save = (key: string, value: unknown) => {
    upsertMutation.mutate(
      { key, value },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Setting saved — it applies right now, no redeploy`), "success");
        },
        onError: () => showToast(t(msg`Could not save the setting`), "error"),
      },
    );
  };

  const reset = (key: string) => {
    deleteMutation.mutate(
      { key },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Setting reset to its environment default`), "success");
        },
        onError: () => showToast(t(msg`Could not reset the setting`), "error"),
      },
    );
  };

  const groupLabel = (group: string): string => {
    switch (group) {
      case "limits": {
        return t(msg`Operational limits`);
      }
      case "retention": {
        return t(msg`Data retention`);
      }
      case "security": {
        return t(msg`Login security`);
      }
      case "i18n": {
        return t(msg`Languages`);
      }
      case "modules": {
        return t(msg`Modules`);
      }
      case "academy": {
        return t(msg`Academy defaults`);
      }
      default: {
        return t(msg`Billing operations`);
      }
    }
  };

  const inGroup = (group: string) => catalog().filter((k) => k.key.startsWith(`${group}.`));

  return (
    <div class="space-y-6" data-testid="app-config-manager">
      <div class="flex items-center gap-3">
        <SlidersHorizontal class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Operational settings`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Limits, retention, policies and module toggles. Environment variables are the defaults; every change here overrides them live, with no redeploy.`,
            )}
          </p>
        </div>
      </div>

      {/* «No hay respuesta» ≠ «no hay nada» (P23/CC-60 · I3). */}
      <Show when={query.isError}>
        <Card class="border-destructive/50" data-testid="admin-config-error">
          <CardContent class="flex items-center justify-between gap-4 p-4 text-sm">
            <span>{t(msg`Could not load the operational settings.`)}</span>
            <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
              {t(msg`Retry`)}
            </Button>
          </CardContent>
        </Card>
      </Show>

      <Show when={!query.isError}>
        <Show
          when={!query.isLoading && resolved() && envDefaults()}
          fallback={
            <div class="space-y-3">
              <Skeleton class="h-32 w-full rounded-xl" />
              <Skeleton class="h-64 w-full rounded-xl" />
            </div>
          }
        >
          <For each={GROUPS}>
            {(group) => (
              <Show when={inGroup(group).length > 0}>
                <Card data-testid={`config-group-${group}`}>
                  <CardHeader>
                    <CardTitle>{groupLabel(group)}</CardTitle>
                  </CardHeader>
                  <CardContent class="space-y-5">
                    <For each={inGroup(group)}>
                      {(spec) => (
                        <ConfigRow
                          spec={spec}
                          settings={resolved()!}
                          overridden={overriddenKeys().has(spec.key)}
                          canWrite={canWrite()}
                          onSave={save}
                          onReset={reset}
                        />
                      )}
                    </For>
                  </CardContent>
                </Card>
              </Show>
            )}
          </For>
        </Show>
      </Show>
    </div>
  );
}

interface RowProps {
  spec: ConfigKeyInfo;
  settings: OperationalSettings;
  overridden: boolean;
  canWrite: boolean;
  onSave: (key: string, value: unknown) => void;
  onReset: (key: string) => void;
}

function ConfigRow(props: RowProps) {
  const { t } = useI18n();
  const key = () => props.spec.key;

  const boolValue = () => BOOL_READ[key()]?.(props.settings) ?? false;
  const listValue = () => (LIST_READ[key()]?.(props.settings) ?? []).join(", ");
  const numberValue = () => String(READ[key()]?.(props.settings) ?? 0);

  const [draft, setDraft] = createSignal<string | null>(null);
  const current = () => (props.spec.kind === "strList" ? listValue() : numberValue());
  const shown = () => draft() ?? current();

  const commit = () => {
    const raw = shown().trim();
    if (props.spec.kind === "strList") {
      props.onSave(
        key(),
        raw
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean),
      );
    } else {
      const n = Number.parseInt(raw, 10);
      if (Number.isNaN(n)) {
        return;
      }
      props.onSave(key(), n);
    }
    setDraft(null);
  };

  return (
    <div class="space-y-2 border-b border-border/50 pb-4 last:border-0 last:pb-0">
      <div class="flex flex-wrap items-center gap-2">
        <Label for={`cfg-${key()}`} class="font-mono text-xs">
          {key()}
        </Label>
        {/* ORIGIN: without this the operator doesn't know if they set what they see or the env did. */}
        <Badge variant={props.overridden ? "default" : "secondary"}>
          {props.overridden ? t(msg`override`) : t(msg`env / default`)}
        </Badge>
        {/* Level: this sets the CEILING; each academy decides within it. */}
        <Show when={props.spec.level === "org"}>
          <Badge variant="outline" data-testid={`config-level-${key()}`}>
            {t(msg`ceiling for every academy`)}
          </Badge>
        </Show>
      </div>

      <p class="text-xs text-muted-foreground">
        {/* EXPLICIT UNIT + equivalent env var: the antidote to the MB↔bytes gotcha. */}
        <span class="font-mono">{props.spec.env}</span>
        {props.spec.envUnit ? ` (${props.spec.envUnit})` : ""} · {props.spec.unit}
      </p>

      <div class="flex items-center gap-2">
        <Show
          when={props.spec.kind !== "bool"}
          fallback={
            <span data-testid={`config-switch-${key()}`}>
              <Switch
                checked={boolValue()}
                disabled={!props.canWrite}
                onCheckedChange={(v: boolean) => props.onSave(key(), v)}
              />
            </span>
          }
        >
          <TextField
            id={`cfg-${key()}`}
            data-testid={`config-input-${key()}`}
            class="max-w-xs"
            value={shown()}
            disabled={!props.canWrite}
            onInput={(e) => setDraft(e.currentTarget.value)}
          />
          <Button
            size="sm"
            variant="outline"
            disabled={!props.canWrite || shown() === current()}
            onClick={commit}
            data-testid={`config-save-${key()}`}
          >
            {t(msg`Save`)}
          </Button>
        </Show>

        <Show when={props.overridden}>
          <Button
            size="sm"
            variant="ghost"
            disabled={!props.canWrite}
            onClick={() => props.onReset(key())}
            data-testid={`config-reset-${key()}`}
          >
            <RotateCcw class="mr-1 h-3 w-3" />
            {t(msg`Reset to env`)}
          </Button>
        </Show>
      </div>
    </div>
  );
}
